'use client';

/**
 * WebQASourceCompareCard — Reception "source ↔ derived" side-by-side.
 *
 * Left: the raw source document — engineer-authored markdown bytes from
 * GCS, via the docs.source strategy (same fetch as the source-doc card;
 * React Query dedupes by media_sid so it's one round-trip even with both
 * cards mounted). Right: the :SemanticProfile's derived summary — the
 * ~5000-word index tier (`response.summary.fast.index`; falls back to
 * `.long` when a doc has no index summary). The CQRS-clean "engineer
 * prose ↔ customer-voice translation" pair, side by side, for eyeball
 * comparison. Both for the top frontier doc (the one summary.fast came
 * from). Behaviour-only — reads useDocsStore + fires docs.source.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { Columns2, Loader2, FileText, AlertCircle } from 'lucide-react';
import { useDocsStore } from '@/lib/stores/docs-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';
import type { DocsSourceArtefact } from '@/lib/api/types';
import { QA_SECTION, QA_BOX } from './web-qa-chrome';


const wordCount = (s?: string | null): number => (s && s.trim() ? s.trim().split(/\s+/).length : 0);
const fmtBytes = (n?: number | null): string =>
  typeof n === 'number' ? (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`) : '—';

const PANE = 'text-[11px] whitespace-pre-wrap break-words font-sans text-foreground/90 max-h-[32rem] overflow-auto rounded border border-border/40 bg-background/50 p-3';


export function WebQASourceCompareCard() {
  const T = useCardTypography();
  const { response } = useDocsStore();
  const sourceStrategy =
    useCapabilityStore((s) => s.strategyName('docs', 'source')) ?? 'docs_source';
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const fast = response?.summary?.fast ?? null;
  const mediaSid = fast?.media_sid ?? null;
  const sourcePath = fast?.source_path ?? null;
  const hasIndex = Boolean(fast?.index && fast.index.trim());
  const derived = hasIndex ? (fast!.index as string) : (fast?.long ?? '');

  const { data, isLoading, error } = useQuery<DocsSourceArtefact>({
    queryKey: ['docs', 'source', mediaSid, sourceStrategy, lang],
    queryFn: async () => {
      if (!sourceStrategy || !mediaSid) throw new Error('docs.source strategy or media_sid missing');
      const res = await api.getStrategyData<DocsSourceArtefact>(sourceStrategy, lang ? { media_sid: mediaSid, lang } : { media_sid: mediaSid });
      if (!res.success) throw new Error(res.error || `${sourceStrategy} failed`);
      return res.data;
    },
    enabled: Boolean(mediaSid && sourceStrategy),
    staleTime: 5 * 60_000,
  });

  // Substrate-presence-driven — nothing to compare until there's a
  // response with a media_sid. The source-doc card has the same gate.
  if (!response || !mediaSid) return null;

  const sourceContent = data?.content ?? '';

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <div className={QA_BOX}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className={cn(T.metadata, 'uppercase tracking-wider text-xs')}>Source ↔ Derived</div>
            <span className={cn(T.metadata, 'font-mono text-xs text-muted-foreground break-all')}>
              {sourcePath ?? mediaSid}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* LEFT — raw source (engineer-authored) */}
            <div className="flex flex-col gap-1 min-w-0">
              <div className={cn(T.metadata, 'text-xs flex items-center gap-1.5 text-muted-foreground')}>
                <FileText className="w-3.5 h-3.5" />
                Source · {fmtBytes(data?.bytes_len)} · engineer-authored
              </div>
              {isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className={T.body}>loading source…</span>
                </div>
              ) : error ? (
                <div className="flex items-center gap-2 text-orange-600 py-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className={cn(T.metadata, 'text-xs')}>{String(error)}</span>
                </div>
              ) : (
                <pre className={cn(T.metadata, PANE)}>{sourceContent || '(empty)'}</pre>
              )}
            </div>

            {/* RIGHT — derived :SemanticProfile summary (customer-voice) */}
            <div className="flex flex-col gap-1 min-w-0">
              <div className={cn(T.metadata, 'text-xs flex items-center gap-1.5 text-muted-foreground')}>
                <Columns2 className="w-3.5 h-3.5" />
                Derived (SemanticProfile) · {wordCount(derived).toLocaleString()} words
                {!hasIndex && fast?.long ? ' · long tier (no index summary)' : ''}
              </div>
              <pre className={cn(T.metadata, PANE)}>{derived || '(no derived summary for this doc)'}</pre>
            </div>
          </div>

          <p className={cn(T.metadata, 'text-[11px] text-muted-foreground/60 border-t border-border/40 pt-2')}>
            CQRS-clean: the source is the deterministic mirror; the derived summary is the recomputable,
            customer-voice translation that gets embedded for retrieval. One doc, two artefacts.
          </p>
        </div>
      </div>
    </section>
  );
}
